<template>
  <div class="editor">
    <p
      v-if="label"
      :class="['editor__title v-label mb-2 mt-0', {'error--text': errorMessages.length > 0}, {'pb-2': !inlineRow}]"
    >
      {{ label }}
    </p>
    <div :class="[{'w-100 d-flex flex-row-reverse': inlineRow}]">
      <input-dialog
        v-if="linkInputDialog"
        v-model="linkUrl"
        label-title="Link"
        rules="required|url"
        title="Add Link"
        :visible="linkInputDialog"
        @close="linkInputDialog = false"
        @confirm="editorToggleLink"
      />
      <div class="menubar d-flex flex-row flex-wrap flex-grow-0 flex-shrink-0 row-gap-2">
        <slot
          name="menubar"
          :editor="editor"
        />
        <div
          v-if="richtextAllowed || boldAllowed || italicAllowed || hrAllowed || linkAllowed || alignAllowed"
          class="d-flex flex-row flex-wrap row-gap-2"
        >
          <filled-button
            v-if="richtextAllowed || boldAllowed"
            class="mr-2"
            :filled="isBoldMark"
            icon="mdi-format-bold"
            :tooltip="$t('button.bold')"
            @click="editor?.chain().focus().toggleBold().run()"
          />
          <filled-button
            v-if="richtextAllowed || italicAllowed"
            class="mr-2"
            :filled="isItalicMark"
            icon="mdi-format-italic"
            :tooltip="$t('button.italic')"
            @click="editor?.chain().focus().toggleItalic().run()"
          />

          <div
            v-if="richtextAllowed || alignAllowed"
            class="align-btn-group"
          >
            <filled-button
              :filled="isLeftMark"
              icon="mdi-format-align-left"
              :tooltip="$t('button.align_text_left')"
              @click="editor?.chain().focus().setTextAlign('left').run()"
            />
            <filled-button
              :filled="isCenterMark"
              icon="mdi-format-align-center"
              :tooltip="$t('button.align_text_center')"
              @click="editor?.chain().focus().setTextAlign('center').run()"
            />
            <filled-button
              class="mr-2"
              :filled="isRightMark"
              icon="mdi-format-align-right"
              :tooltip="$t('button.align_text_right')"
              @click="editor?.chain().focus().setTextAlign('right').run()"
            />
          </div>

          <filled-button
            v-if="richtextAllowed || hrAllowed"
            class="mr-2"
            icon="mdi-minus"
            :tooltip="$t('button.horizontal_rule')"
            @click="editor?.chain().focus().setHorizontalRule().run()"
          />
          <filled-button
            v-if="richtextAllowed || linkAllowed"
            class="mr-2"
            :disabled="isHyperlink || !isHyperlink && editor?.view.state.selection.empty"
            icon="mdi-link-variant"
            :tooltip="$t('button.add_hyperlink')"
            @click="linkInputDialog = true"
          />
          <filled-button
            v-if="richtextAllowed || linkAllowed"
            class="mr-2"
            :disabled="!isHyperlink"
            icon="mdi-link-variant-off"
            :tooltip="$t('button.remove_hyperlink')"
            @click="editor?.chain().focus().unsetLink().run()"
          />
        </div>
      </div>

      <editor-content
        :class="[
          {'editor__content-dark': dark},
          {'editor__content-error': errorMessages.length > 0},
          {'mt-2': dense && !inlineRow},
          {'mt-5': !dense && !inlineRow},
          {'mt-0 w-100 mr-4': inlineRow},
          'editor__content text--primary']"
        :editor="editor"
      />
    </div>
    <div class="d-flex w-100 flex-row">
      <p class="field-error-message mt-1 mb-0">
        {{ errorMessages.join(' ') }}&nbsp;
      </p>
      <p class="ml-auto">
        {{ hint }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import Vue, {
  PropType, computed, defineComponent, onBeforeUnmount, ref,
  watch,
} from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-2';
import { Handlebar } from '@/services/handlebar-tiptap-extension';
import { textWithHandlebarsToHtml, htmlToTextWithHandlebars } from '@/services/handlebar-utils';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import MessageHandlebar from '@/model/MessageHandlebar';
import InputDialog from '@/components/dialogs/InputDialog.vue';
import { TranslateResult } from 'vue-i18n';
import { FixedBlock } from '@/services/tiptap-fixedblock';
import { useHandlebars } from '@/composables/messagecenter';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import { useVuetify } from '../helpers/vue2-helpers';

const props = defineProps({
  value: { type: String, required: false, default: '' },
  dense: { type: Boolean, required: false, default: false },
  richtextAllowed: { type: Boolean, required: false, default: false },
  boldAllowed: { type: Boolean, required: false, default: false },
  italicAllowed: { type: Boolean, required: false, default: false },
  hrAllowed: { type: Boolean, required: false, default: false },
  linkAllowed: { type: Boolean, required: false, default: false },
  alignAllowed: { type: Boolean, required: false, default: false },

  encodeLinebreak: { type: Boolean, required: false, default: false },
  errorMessages: { type: Array<String>, required: false, default: [] },
  hint: { type: String, required: false, default: '' },
  height: { type: String, required: false, default: '200px' },
  label: { type: [String, Object] as PropType<string|TranslateResult>, required: false, default: undefined },
  inlineRow: { type: Boolean, required: false, default: false },
});

const emit = defineEmits<{(e:'input', event: string | null): void}>();

const vuetify = useVuetify();

const editor = ref(undefined as undefined | Editor);

const handlebars = useHandlebars;

const linkInputDialog = ref(false);
const linkUrl = ref(undefined as undefined | string);

const editorToggleLink = (href: string) => {
  editor.value?.commands.toggleLink({ href, target: '_blank' });
};

const dark = computed(() => vuetify.theme.dark);

const isBoldMark = computed(() => editor.value!.isActive('bold') || false);
const isItalicMark = computed(() => editor.value!.isActive('italic') || false);
const isHyperlink = computed(() => editor.value!.isActive('link') || false);

const isLeftMark = computed(() => editor.value?.isActive({ textAlign: 'left' }) || false);
const isCenterMark = computed(() => editor.value?.isActive({ textAlign: 'center' }) || false);
const isRightMark = computed(() => editor.value?.isActive({ textAlign: 'right' }) || false);

const handlebarsById = computed(() => store.getters.messageCenterHandlebarsById);

watch(() => props.value, () => {
  if (editor.value!.getHTML() === textWithHandlebarsToHtml(props.value, handlebars.value)) { return; }
  editor.value!.commands.setContent(textWithHandlebarsToHtml(props.value, handlebars.value));
});

// @ts-ignore
editor.value = new Editor({
  extensions: [
    StarterKit,
    Link,
    Handlebar,
    FixedBlock,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
  ],
  content: textWithHandlebarsToHtml(props.value, handlebars.value),
  onUpdate: ({ editor: e }) => {
    const content = e.getHTML();
    let newContent = htmlToTextWithHandlebars(content, handlebarsById.value);
    if (props.encodeLinebreak && newContent) {
      newContent = newContent.replaceAll(/\n/g, '<br />');
    }
    emit('input', newContent);
  },
});

onBeforeUnmount(() => {
  editor.value!.destroy();
  editor.value = undefined;
});

const svgBackground = computed(() => {
  const text = i18n.t('label.reservation_details');
  const fgColor = vuetify.theme.dark ? '#D6D6D8' : '#53535D';
  const svg = `
<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 128'>
  <rect width='400' height='128' fill='transparent'/>
  <rect x='20' y='12' width='360' height='1' fill='#d7d7d7' rx='1'/>
  <text x='200' y='32' text-anchor='middle' fill='${fgColor}' font-size='12'\
  font-family='Source Sans Pro' font-weight='medium'>${text}</text>
  <rect x='60'  y='46' width='58'  height='12' rx='6' fill='#C6C6C8'/>
  <rect x='170' y='46' width='58'  height='12' rx='6' fill='#C6C6C8'/>
  <rect x='280' y='46' width='58'  height='12' rx='6' fill='#C6C6C8'/>
  <rect x='50'  y='64' width='78'  height='10' rx='6' fill='#EAEAEB'/>
  <rect x='160' y='64' width='78'  height='10' rx='6' fill='#EAEAEB'/>
  <rect x='270' y='64' width='78'  height='10' rx='6' fill='#EAEAEB'/>
  <rect x='140' y='86' width='120' height='20' rx='4' fill='#C6C6C8'/>
  <rect x='20' y='116' width='360' height='1' fill='#d7d7d7' rx='1'/>
</svg>
  `;

  // Remove only line breaks and trim
  const cleanSvg = svg.replace(/\n/g, '').trim();

  // Encode the whole SVG
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(cleanSvg)}")`;
});
</script>

<style lang="scss">

.editor__title {
  font-size: 12px;
}

.editor__content {
  color: $black-color !important;
  border-bottom: 1px solid;
  outline: none;
  padding: 8px;
  min-height: v-bind(height);
  max-height: 100%;
  overflow-y: scroll;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.50) !important;

  &.editor__content-error {
    border: 1px solid $red-color !important;
  }
}

.editor__content-dark {
  border: 1px solid white !important;
}

.ProseMirror {
  min-height: v-bind(height);
  max-height: 100%;
}

.editor__content p {
  margin-bottom: 0px !important;
}

.editor__content {
  -webkit-touch-callout: auto;
  -webkit-user-select: auto;
  -khtml-user-select: auto;
  -moz-user-select: auto;
  -ms-user-select: auto;
  user-select: auto;
}

.ProseMirror {
  outline: none;
}

.handlebar {
  background: $primary-color;
  color: $white-color;
  font-size: 0.8rem;
  font-weight: bold;
  border-radius: 3px;
  margin-top: 1px;
  margin-bottom: 1px;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
  white-space: nowrap;
  height: 24px;
  display: inline-block;
}

  .tiptap-fixed-block::after, span[data-type="fixed-block"]::after {
    content: 'This is it';
    position: absolute;
    left: 50%;
    bottom: calc(100% + 6px); /* place above the span with 6px gap */
    transform: translateX(-50%);
    white-space: nowrap;
    padding: 4px 6px;
    font-size: 12px;
    border-radius: 3px;
    background: #000;
    color: #fff;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.12s ease;
    z-index: 999;
  }

  .tiptap-fixed-block:hover::after, span[data-type="fixed-block"]:hover::after {
    opacity: 1;
  }

.tiptap-fixed-block, span[data-type="fixed-block"] {
  .theme--dark &{
    background-color: #3F3F49;
  }
  color: transparent;
  font-size: 1rem;
  font-weight: 500;
  border-radius: 3px;
  margin-top: 1px;
  margin-bottom: 1px;
  padding-left: 0.2rem;
  padding-right: 0.2rem;
  align-content: center;
  text-align: center;
  height: 48px;
  display: inline-block;
  width: 100%;
  text-transform: uppercase;
  cursor: not-allowed;
  user-select: none;
  white-space: nowrap;

  white-space: normal;
  overflow: hidden;
  height: 128px;
  padding-bottom: 12px;

  background-color: #F9F9F9;
  padding-top: 135px;

  background-image: v-bind(svgBackground);

  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  border-radius: 8px;
}

.align-btn-group {

  > div:nth-child(1) > button.v-btn {
    border-top-right-radius: 0px;
    border-bottom-right-radius: 0px;
    border-right: none;
  }

  > div:nth-child(2) > button.v-btn  {
    border-radius: 0px;
    border-right: none;
  }

  > div:nth-child(3) > button.v-btn  {
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
  }
}

</style>
